import { Injectable } from '@angular/core';
import { ShopPluginApi } from '@app/core/services/api/plugin-api.service';
import { CheckoutApiService } from '@app/pages/checkout-app/services/api/checkout.api.service';
import { Shop } from '@pdqdev/pdq-shared/dist/entities/Shop';
import { ShopPlugin } from '@pdqdev/pdq-shared/dist/interfaces';
import { ReplaySubject, Observable, concat, of } from 'rxjs';
import { first, last, map, switchMap, tap } from 'rxjs/operators';

export interface ProductPromiseListItem {
  id: number;
  internalName: string;
  internalDescription?: string;
  isActive: boolean;
  testMode?: boolean;
  usageToken?: string;
  createdAt?: string;
}

type FormStateIconOptions = { iconUrl?: string | File; customIconUrl?: string | File };
type FormStateSignal = { id: string; iconUrl?: string | File; customIconUrl?: string | File };
type PendingIconComponent = { id: string; options: FormStateIconOptions & { customIconUrl: string | File } };

const hasComponentFileIcon = (c: unknown): boolean =>
  (c as { options?: FormStateIconOptions }).options?.customIconUrl instanceof File;

const getPendingSignalsForComponent = (c: unknown): FormStateSignal[] => {
  const signals = (c as { options?: { signals?: unknown } }).options?.signals;
  if (!Array.isArray(signals)) return [];
  return (signals as FormStateSignal[]).filter((s) => s?.customIconUrl instanceof File);
};

const hasComponentPendingSignals = (c: unknown): boolean => getPendingSignalsForComponent(c).length > 0;

@Injectable({
  providedIn: 'root',
})
export class ProductPromiseService {
  constructor(
    private pluginApi: ShopPluginApi,
    private checkoutApiService: CheckoutApiService,
  ) {
    this.initProductPageSettings();
  }

  listEnabledShippingMethodNames(): Observable<string[]> {
    return this.checkoutApiService.get('admin/v1/shippings?sortOrder=desc&limit=99999&deleted=false&enabled=true').pipe(
      map(
        (response: {
          results?: {
            name?: string;
            fallbackMethod?: boolean | null;
            displayEstDelivery?: boolean | null;
            displayEstimatedDeliveryBusinessDays?: boolean | null;
          }[];
        }) => {
          const names = (response?.results ?? [])
            .filter((s) => !s?.fallbackMethod && (s?.displayEstDelivery || s?.displayEstimatedDeliveryBusinessDays))
            .map((s) => s?.name)
            .filter((n): n is string => !!n);
          return Array.from(new Set(names));
        },
      ),
    );
  }
  private productPageSettings = new ReplaySubject<ShopPlugin.ProductPagePromise.Config | null>(null);
  get productPageSettings$() {
    return this.productPageSettings.asObservable();
  }

  initProductPageSettings() {
    this.pluginApi
      .get('/admin/product-promise/settings')
      .subscribe((response: ShopPlugin.ProductPagePromise.Config) => {
        this.productPageSettings.next(response);
      });
  }

  list(): Observable<ProductPromiseListItem[]> {
    return this.pluginApi.get('/admin/product-promise/settings');
  }

  getById(id: number): Observable<ShopPlugin.ProductPagePromise.Config> {
    return this.pluginApi.get(`/admin/product-promise/settings/${id}`);
  }

  saveIcon(id: number | null, config: ShopPlugin.ProductPagePromise.Config) {
    return new Observable((subscriber) => {
      if (typeof config.design.iconUrl === 'string') {
        subscriber.next(config.design.iconUrl);
        return;
      }
      if (!id) {
        subscriber.error(new Error('Promise ID is required to save icon'));
        return;
      }
      const file = config.design.iconUrl;
      const formData = new FormData();
      formData.append('file', file);

      this.pluginApi.patch(`/admin/product-promise/settings/${id}/icon`, formData).subscribe(
        (res) => {
          subscriber.next(res.icon);
        },
        (err) => {
          subscriber.error(err);
        },
      );
    });
  }

  saveComponentIcon(promiseId: number, componentId: string, file: File, signalId?: string): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({ componentId });
    if (signalId) params.set('signalId', signalId);
    return this.pluginApi
      .patch(`/admin/product-promise/settings/${promiseId}/icon?${params.toString()}`, formData)
      .pipe(map((res: { icon: string }) => res.icon));
  }

  private uploadPendingComponentIcons(
    promiseId: number,
    config: ShopPlugin.ProductPagePromise.Config,
  ): Observable<ShopPlugin.ProductPagePromise.Config> {
    const components = config.advancedLayoutSettings?.components ?? [];
    const componentUploads: Observable<string>[] = [];

    for (const component of components) {
      if (hasComponentFileIcon(component)) {
        const c = component as unknown as PendingIconComponent;
        componentUploads.push(
          this.saveComponentIcon(promiseId, c.id, c.options.customIconUrl as File).pipe(
            tap((url) => {
              const wasSelected = c.options.iconUrl === c.options.customIconUrl;
              c.options.customIconUrl = url;
              if (wasSelected) {
                c.options.iconUrl = url;
              }
            }),
          ),
        );
      }
      const pendingSignals = getPendingSignalsForComponent(component);
      for (const signal of pendingSignals) {
        componentUploads.push(
          this.saveComponentIcon(promiseId, component.id, signal.customIconUrl as File, signal.id).pipe(
            tap((url) => {
              const wasSelected = signal.iconUrl === signal.customIconUrl;
              signal.customIconUrl = url;
              if (wasSelected) {
                signal.iconUrl = url;
              }
            }),
          ),
        );
      }
    }

    if (!componentUploads.length) return of(config);
    return concat(...componentUploads).pipe(
      last(),
      map(() => config),
    );
  }

  private hasPendingComponentIcons(config: ShopPlugin.ProductPagePromise.Config): boolean {
    const components = config.advancedLayoutSettings?.components ?? [];
    return components.some((c) => hasComponentFileIcon(c) || hasComponentPendingSignals(c));
  }

  private stripPendingComponentIconsForInitialPost(
    config: ShopPlugin.ProductPagePromise.Config,
  ): ShopPlugin.ProductPagePromise.Config {
    if (!config.advancedLayoutSettings?.components?.length) return config;
    return {
      ...config,
      advancedLayoutSettings: {
        ...config.advancedLayoutSettings,
        components: config.advancedLayoutSettings.components.map((c) => {
          const hasComponentFile = hasComponentFileIcon(c);
          const hasSignalFile = hasComponentPendingSignals(c);
          if (!hasComponentFile && !hasSignalFile) return c;
          const options = (c as { options: FormStateIconOptions & { signals?: FormStateSignal[] } }).options ?? {};
          let updatedOptions: typeof options = options;
          if (hasComponentFile) {
            const cleanedIconUrl = options.iconUrl === options.customIconUrl ? '' : options.iconUrl;
            updatedOptions = { ...updatedOptions, customIconUrl: '', iconUrl: cleanedIconUrl };
          }
          if (hasSignalFile && Array.isArray(options.signals)) {
            updatedOptions = {
              ...updatedOptions,
              signals: options.signals.map((s) => {
                if (!(s?.customIconUrl instanceof File)) return s;
                const cleanedSignalIconUrl = s.iconUrl === s.customIconUrl ? '' : s.iconUrl;
                return { ...s, customIconUrl: '', iconUrl: cleanedSignalIconUrl };
              }),
            };
          }
          return { ...c, options: updatedOptions } as typeof c;
        }),
      },
    };
  }

  setProductPageSettings(
    config: ShopPlugin.ProductPagePromise.Config,
    shop: Shop,
  ): Observable<ShopPlugin.ProductPagePromise.Config> {
    const configId = (config as any).id;
    const hasComponentIcons = this.hasPendingComponentIcons(config);

    if (configId && hasComponentIcons) {
      return this.uploadPendingComponentIcons(configId, config).pipe(
        switchMap((updatedConfig) => this.runSetProductPageSettings(updatedConfig, shop)),
      );
    }
    if (!configId && hasComponentIcons) {
      return this.createNewPromiseWithComponentIcons(config, shop);
    }
    return this.runSetProductPageSettings(config, shop);
  }

  private createNewPromiseWithComponentIcons(
    config: ShopPlugin.ProductPagePromise.Config,
    shop: Shop,
  ): Observable<ShopPlugin.ProductPagePromise.Config> {
    const hasDesignIconFile = config.design?.iconUrl && typeof config.design.iconUrl !== 'string';
    const stripped = this.stripPendingComponentIconsForInitialPost(config);
    const initialPostBody = hasDesignIconFile
      ? { ...stripped, design: { ...stripped.design, iconUrl: '' }, shop: { id: shop.id, name: shop.name } }
      : { ...stripped, shop: { id: shop.id, name: shop.name } };
    return (
      this.pluginApi.post(
        '/admin/product-promise/settings',
        initialPostBody,
      ) as Observable<ShopPlugin.ProductPagePromise.Config>
    ).pipe(
      switchMap((createdConfig) => {
        const createdId = (createdConfig as any).id;
        const configWithFiles: ShopPlugin.ProductPagePromise.Config = {
          ...createdConfig,
          design: config.design,
          advancedLayoutSettings: config.advancedLayoutSettings,
        };
        return this.uploadPendingComponentIcons(createdId, configWithFiles).pipe(
          switchMap((withComponentUrls) =>
            this.saveIcon(createdId, withComponentUrls).pipe(
              switchMap((iconUrl) =>
                this.pluginApi.patch(`/admin/product-promise/settings/${createdId}`, {
                  ...withComponentUrls,
                  design: { ...withComponentUrls.design, iconUrl },
                  shop: { id: shop.id, name: shop.name },
                }),
              ),
            ),
          ),
        );
      }),
      tap((updatedConfig) => this.productPageSettings.next(updatedConfig as ShopPlugin.ProductPagePromise.Config)),
      first(),
    ) as Observable<ShopPlugin.ProductPagePromise.Config>;
  }

  private runSetProductPageSettings(
    config: ShopPlugin.ProductPagePromise.Config,
    shop: Shop,
  ): Observable<ShopPlugin.ProductPagePromise.Config> {
    return new Observable((subscriber) => {
      const configId = (config as any).id;
      const hasIconFile = config.design?.iconUrl && typeof config.design.iconUrl !== 'string';

      if (hasIconFile && configId) {
        this.saveIcon(configId, config)
          .pipe(
            switchMap((iconUrl: string) => {
              config.design.iconUrl = iconUrl;
              return this.pluginApi.patch(`/admin/product-promise/settings/${configId}`, {
                ...config,
                shop: { id: shop.id, name: shop.name },
              });
            }),
          )
          .subscribe(
            (updatedConfig: ShopPlugin.ProductPagePromise.Config) => {
              this.productPageSettings.next(updatedConfig);
              subscriber.next(updatedConfig);
            },
            (err) => subscriber.error(err),
          );
      } else if (hasIconFile && !configId) {
        this.pluginApi
          .post('/admin/product-promise/settings', {
            ...config,
            design: { ...config.design, iconUrl: '' },
            shop: { id: shop.id, name: shop.name },
          })
          .pipe(
            switchMap((createdConfig: ShopPlugin.ProductPagePromise.Config) => {
              const createdId = (createdConfig as any).id;
              return this.saveIcon(createdId, config).pipe(
                switchMap((iconUrl: string) => {
                  return this.pluginApi.patch(`/admin/product-promise/settings/${createdId}`, {
                    ...createdConfig,
                    design: { ...createdConfig.design, iconUrl },
                    shop: { id: shop.id, name: shop.name },
                  });
                }),
              );
            }),
          )
          .subscribe(
            (updatedConfig: ShopPlugin.ProductPagePromise.Config) => {
              this.productPageSettings.next(updatedConfig);
              subscriber.next(updatedConfig);
            },
            (err) => subscriber.error(err),
          );
      } else {
        if (configId) {
          this.pluginApi
            .patch(`/admin/product-promise/settings/${configId}`, {
              ...config,
              shop: { id: shop.id, name: shop.name },
            })
            .subscribe(
              (updatedConfig: ShopPlugin.ProductPagePromise.Config) => {
                this.productPageSettings.next(updatedConfig);
                subscriber.next(updatedConfig);
              },
              (err) => subscriber.error(err),
            );
        } else {
          this.pluginApi
            .post('/admin/product-promise/settings', {
              ...config,
              shop: { id: shop.id, name: shop.name },
            })
            .subscribe(
              (updatedConfig: ShopPlugin.ProductPagePromise.Config) => {
                this.productPageSettings.next(updatedConfig);
                subscriber.next(updatedConfig);
              },
              (err) => subscriber.error(err),
            );
        }
      }
    }).pipe(first()) as Observable<ShopPlugin.ProductPagePromise.Config>;
  }

  update(
    id: number,
    config: Partial<ShopPlugin.ProductPagePromise.Config>,
    shop: Shop,
  ): Observable<ShopPlugin.ProductPagePromise.Config> {
    if (this.hasPendingComponentIcons(config as ShopPlugin.ProductPagePromise.Config)) {
      return this.uploadPendingComponentIcons(id, config as ShopPlugin.ProductPagePromise.Config).pipe(
        switchMap((updatedConfig) => this.runUpdate(id, updatedConfig, shop)),
      );
    }
    return this.runUpdate(id, config, shop);
  }

  private runUpdate(
    id: number,
    config: Partial<ShopPlugin.ProductPagePromise.Config>,
    shop: Shop,
  ): Observable<ShopPlugin.ProductPagePromise.Config> {
    return new Observable((subscriber) => {
      const configWithIcon = { ...config };
      if (config.design?.iconUrl && typeof config.design.iconUrl !== 'string') {
        this.saveIcon(id, config as ShopPlugin.ProductPagePromise.Config)
          .pipe(
            switchMap((iconUrl: string) => {
              configWithIcon.design = { ...configWithIcon.design, iconUrl };
              return this.pluginApi.patch(`/admin/product-promise/settings/${id}`, {
                ...configWithIcon,
                shop: { id: shop.id, name: shop.name },
              });
            }),
          )
          .subscribe(
            (updatedConfig: ShopPlugin.ProductPagePromise.Config) => {
              subscriber.next(updatedConfig);
            },
            (err) => subscriber.error(err),
          );
      } else {
        this.pluginApi
          .patch(`/admin/product-promise/settings/${id}`, {
            ...configWithIcon,
            shop: { id: shop.id, name: shop.name },
          })
          .subscribe(
            (updatedConfig: ShopPlugin.ProductPagePromise.Config) => {
              subscriber.next(updatedConfig);
            },
            (err) => subscriber.error(err),
          );
      }
    }).pipe(first()) as Observable<ShopPlugin.ProductPagePromise.Config>;
  }

  delete(id: number): Observable<void> {
    return this.pluginApi.delete(`/admin/product-promise/settings/${id}`);
  }

  duplicate(id: number): Observable<ShopPlugin.ProductPagePromise.Config> {
    return this.pluginApi.post(`/admin/product-promise/settings/${id}/duplicate`, {});
  }

  activate(id: number): Observable<ShopPlugin.ProductPagePromise.Config> {
    return this.pluginApi.patch(`/admin/product-promise/settings/${id}/activate`, {});
  }
}
